import { IPromise } from 'angular';
import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';

import { Messages } from '../../codegen/Localize';
import { metricsDataSaga } from './MetricsDataSaga';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { IMetricInfo } from '../models/IMetricInfo';
import { Metrics } from '../resources/Metrics';
import { SetShowMetricsBannerRequest } from '../../ts/ServerApi/requests/SetShowMetricsBannerRequest';
import { DeleteContentsAction } from '../../ts/services/DeleteContentsAction';
import { ModalInstance, ReactModalService } from '../../ts/services/ReactModalService';
import { ResumeMetricAction } from '../services/ResumeMetricAction';
import { ServerService } from '../../ts/services/ServerService';
import { requestFailed } from '../../app/actions/RequestFailedAction';
import { deleteContentAction } from '../../app/actions/spaces/DeleteContentAction';
import { requestSpaceContents } from '../../app/actions/spaces/RequestSpaceContentsActions';
import {
  HideMetricsBannerAction,
  resumeMetric,
  ResumeMetricAction as ResumeMetricReduxAction,
  ResumeMetricFromExplorerAction,
  HideMetricsBanner,
  ResumeMetric,
  ResumeMetricFromExplorer,
} from '../actions/UpdateMetricsActions';
import { ResumeMetricErrorDialogProps, resumeMetricErrorModalOptions } from '../../app/content/ResumeMetricErrorDialog';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { ServiceLocator } from '../../app/ServiceLocator';
import { UrlParamKeys } from '../../app/UrlParamKeys';

export function* handleResumeMetric(action: ResumeMetricReduxAction) {
  try {
    yield call(resumeSuspendedMetric, action.metric);
    yield call(refreshAfterMetricsActionSucceeded);
  } catch(e) {
    yield call(deleteSuspendedMetric, action.metric);
  }
}

export function* handleResumeMetricFromExplorer(action: ResumeMetricFromExplorerAction): SagaIterator {
  try {
    const [ metricInfo ] = (yield all([
      call(getMetricInfo, action.metricId),
      call(navigateToMetricDetailPage, action.metricId)
    ])) as any; // TFS 1116084 - need to upgrade redux-saga in order to remove 'as any'
    yield put(resumeMetric(metricInfo));
  } catch (e) {
    yield put(requestFailed(e, Messages.unknownFailure()));
  }
}

export function* handleHideMetricsBanner(action: HideMetricsBannerAction) {
  try {
    yield call(dismissMetricsBanner);
    yield call(updateSessionInfo);
  } catch (e) {
    yield put(requestFailed(e, Messages.unknownFailure()));
  }
}

export function resumeSuspendedMetric(metric: IMetricInfo) {
  return ServiceLocator.get<ResumeMetricAction>('ResumeMetricAction')
      .resumeMetric(metric);
}

export function* refreshAfterMetricsActionSucceeded() {
  yield put(requestSpaceContents());
}

export function* deleteSuspendedMetric(metric: IMetricInfo) {
  try {
    const shouldDelete: boolean = yield call(openMetricResumeErrorDialog, metric);
    if (!shouldDelete) {
      return;
    }
    yield put(deleteContentAction(metric as IContentInfo));
  } catch(e) {
    yield put(requestFailed(e, Messages.deleteFailed()));
  }
}

export function confirmDeleteMetric(metric: IMetricInfo) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<DeleteContentsAction>('DeleteContentsAction').confirmDeleteContents([metric as IContentInfo], context);
}

export function openMetricResumeErrorDialog(): IPromise<any> {
  let modalInstance: ModalInstance;

  const props: ResumeMetricErrorDialogProps = {
    submit: () => modalInstance && modalInstance.close(true),
    cancel: () => modalInstance && modalInstance.close(false),
  };

  modalInstance = ReactModalService.open(resumeMetricErrorModalOptions(props));
  return modalInstance.result;
}

export function* navigateToMetricDetailPage(metricId: string) {
  const urls = yield select(siteUrlsSelector);
  const locationService = ServiceLocator.get<ng.ILocationService>('$location');
  const metricPath = urls.metricPath(metricId);
  locationService.url(metricPath).search(UrlParamKeys.SuppressResumeMetricBanner, 't');
}

export async function getMetricInfo(metricId: string): Promise<IMetricInfo> {
  const metricsResource = ServiceLocator.get<Metrics>('Metrics');
  return await metricsResource.getById(metricId);
}

export function dismissMetricsBanner() {
  const serverService = ServiceLocator.get<ServerService>('ServerService');
  return serverService.sendRequest(new SetShowMetricsBannerRequest({ showMetricsBanner: false }));
}

export function updateSessionInfo() {
  const serverService = ServiceLocator.get<ServerService>('ServerService');
  return serverService.refreshSessionInfo();
}

export function* rootMetricSpaceSagas(): SagaIterator {
  yield takeEvery(ResumeMetric, handleResumeMetric);
  yield takeEvery(ResumeMetricFromExplorer, handleResumeMetricFromExplorer);
  yield takeEvery(HideMetricsBanner, handleHideMetricsBanner);
  yield fork(metricsDataSaga);
}
